import React from 'react';
import moment from 'moment';
import connectToStores from 'alt-utils/lib/connectToStores';
import AuthStore from 'stores/AuthStore';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import {addSubtractDayIntervalEndDateExclusive} from '@store/coaching-plans';
import KeyMetric from 'components/dashboard/admin/KeyMetric';
import {formatRollupCell} from 'components/dashboard/rollups/formatRollupCell';
import './AdminKeyMetrics.styl';
import MetricTooltip from 'components/pure/tooltips/MetricTooltip';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import BasedOnDatesLink from 'components/dashboard/common/BasedOnDatesLink';
import {dashV2Path, TOOLTIP_DELAY} from 'components/StatsPage2.utils';

const widgetId = 'admin_key_metrics';
const widgetType = 'ADMIN_KEY_METRICS';

// The keys are the key metric property name in the widget.report object
const keyMetrics = {
  durationOfCoaching: {
    label: 'Hours of Coaching',
    popoverMessage: (
      <MetricTooltip
        header="Hours of Coaching"
        tooltip={`Research shows that when reps receive one hour of coaching per week, their call quality gets better.
          Coach recommends that managers and coaches provide one hour of coaching per rep per week - 30 minutes on review time (listening to, commenting on, and scoring calls), and 30 minutes on coaching meetings.`}
        link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
      />
    ),
    path: params => `/${dashV2Path}/admin/${params.adminId}/coaching-activity`,
    // round before value comparison
    valueConverter: value => Math.round(moment.duration(value).as('seconds') / 3600.0),
    valueFormatter: value => {
      return formatRollupCell(value);
    },
  },
  numCoachingPlans: {
    label: 'Coaching Plans',
    popoverMessage: (
      <MetricTooltip
        header="Coaching Plans"
        tooltip={`Managers and coaches build Coaching Plans to address rep skill gaps. If the number of Coaching Plans created increases, then more coaching is being formally tracked and measured in the Coach platform.
          If the increase in Coaching Plan creation also coincides with an increase in Average Call Score, your managers and coaches are coaching effectively and conversation quality is improving.`}
        link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
      />
    ),
    path: params => `/${dashV2Path}/admin/${params.adminId}/coaching-plans`,
  },
  averageRepScore: {
    label: 'Average Call Score',
    popoverMessage: (
      <MetricTooltip
        header="Average Call Score"
        tooltip="If Average Call Score increases over time, your team's call quality is increasing. If it decreases, you may need to adjust your coaching focus based on your team's strengths and weaknesses. Drill down into how your teams and reps are performing and create a Coaching Plan for any reps with a score of less than 60% on a given skill."
        link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
      />
    ),
    path: params => `/${dashV2Path}/admin/${params.adminId}/team-skills`,
    valueConverter: value => {
      if (value == null) {
        return null;
      } else {
        // round before value comparison
        return Math.round(value);
      }
    },
    valueFormatter: value => {
      if (value == null) {
        return 'N/A';
      } else {
        return `${value}%`;
      }
    },
    newAccountValue: 'N/A',
  },
  callVolume: {
    label: 'Call Volume',
    popoverMessage: (
      <MetricTooltip
        header="Call Volume"
        tooltip={`Call Volume provides insight on the total calling and web conferencing activity across your organization - any conversation that is processed by Coach.
          If you see a continuous decrease in this metric, you may need to address rep activity levels with your managers.`}
        link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
      />
    ),
    path: params => `/${dashV2Path}/admin/${params.adminId}/activity`,
    valueFormatter: value => formatRollupCell(value),
  },
};

@connectToStores
export default class AdminKeyMetrics extends React.PureComponent {
  static getStores() {
    return [AuthStore, ManagerDashboardStore];
  }

  static getPropsFromStores() {
    const widgets = ManagerDashboardStore.getState().get('widgets');
    const widget = widgets && widgets.get(widgetId);
    return {
      widget,
    };
  }

  componentDidMount() {
    this.getWidget();
  }

  componentDidUpdate(prevProps) {
    const {startDate: prevStartDate, endDate: prevEndDate} = prevProps;
    const {startDate, endDate} = this.props;
    if (startDate !== prevStartDate || endDate !== prevEndDate) {
      this.getWidget();
    }
  }

  getWidget() {
    const {startDate, endDate} = this.props;
    // maxDate isn't needed when subtracting.
    const {startDate: previousStartDate, endDate: previousEndDate} =
      addSubtractDayIntervalEndDateExclusive('subtract', startDate, endDate);

    const widgetConfig = {
      widgetId,
      widgetType,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
    };
    ManagerDashboardActions.getWidget.defer([widgetConfig, null]);
  }

  renderKeyMetric(current, previous, metric) {
    const keyMetric = keyMetrics[metric];
    return (
      <GenericTooltip
        hoverBehavior
        positionTop
        delay={TOOLTIP_DELAY}
        tooltip={keyMetric.popoverMessage}>
        <KeyMetric
          adminId={this.props.adminId}
          label={keyMetric.label}
          path={keyMetric.path}
          current={
            keyMetric.valueConverter ? keyMetric.valueConverter(current[metric]) : current[metric]
          }
          previous={
            keyMetric.valueConverter ? keyMetric.valueConverter(previous[metric]) : previous[metric]
          }
          valueFormatter={keyMetric.valueFormatter}
          isNewAccount={this.props.isNewAccount}
          newAccountValue={keyMetric.newAccountValue || 0}
        />
      </GenericTooltip>
    );
  }

  render() {
    const {widget} = this.props;
    if (!widget) {
      return null;
    }

    if (!widget.report) {
      return null;
    }

    const current = widget.report.current;
    const previous = widget.report.previous;
    return (
      <div className="standalone-widget-wrapper key-metrics">
        <div className="widget">
          <header className="widget-head">
            <div className="title-and-dates">
              <h2 className="widget-title">{widget.label}</h2>
              <BasedOnDatesLink
                startDate={this.props.headerStartDate}
                endDate={this.props.headerEndDate}
                location={this.props.location}
              />
            </div>
          </header>
          <div className="metrics">
            {this.renderKeyMetric(current, previous, 'durationOfCoaching')}
            {this.renderKeyMetric(current, previous, 'numCoachingPlans')}
            {this.renderKeyMetric(current, previous, 'averageRepScore')}
            {this.renderKeyMetric(current, previous, 'callVolume')}
          </div>
        </div>
      </div>
    );
  }
}
